import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import AddressGroupV2 from 'components/field/AddressGroupV2';
import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { BRANCHES } from 'services/consts';

import { getPickupLocationCountries } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const BranchFormshort = NiceModal.create(({ modalName, setBranch }) => {
	const allowedCountriesStr = useSelector((state) => state.user.localization.allowedCountries);

	const { Country } = useLocale();

	const allowedCountries = useMemo(
		() => (allowedCountriesStr ? [Country, ...allowedCountriesStr.split(',')] : [Country]),
		[allowedCountriesStr]
	);
	const { planName = null } = useSelector((state) => state.user);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().min(3).trim().required(),
					address: yup.object().shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().isCountryAllowed().nullable().required(),
						stateName: yup.string().nullable(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						neighbourhoodName: yup.string().nullable(),
						street: yup.string().nullable(),
						buildingName: yup.string().nullable(),
						lat: yup.number().min(-90).max(90).nullable(),
						lng: yup.number().min(-180).max(180).nullable(),
						zipCode: yup.string().nullable(),
						addressInputType: yup.string().nullable()
					}),
					person: yup.string().nullable().required(),
					email: yup.string().email().nullable().required(),
					phone: yup.string().nullable().required().phoneNumber(),
					servingRadius: yup.number().positive().nullable().required()
				})
				.required(),
		[]
	);
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const [isLoading, setIsLoading] = useState(false);

	const dispatch = useDispatch();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			name: null,
			address: {
				text: null,
				countryName: Country,
				stateName: null,
				cityName: null,
				districtName: null,
				neighbourhoodName: null,
				street: null,
				buildingName: null,
				zipCode: null,
				lat: null,
				lng: null,
				addressInputType: null
			},
			person: null,
			email: null,
			phone: null,
			servingRadius: 1,
			status: 'active'
		}
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = (data) => {
		setIsLoading(true);
		// ID means we are in edit mode else is add new branch.
		const URL = BRANCHES.ADD_BRANCH;

		axiosPrivate
			.post(URL, {
				name: data.name,
				address: {
					text: data.address.text ?? null,
					countryName: data.address.countryName ?? null,
					stateName: data.address.stateName ?? null,
					cityName: data.address.cityName ?? null,
					districtName: data.address.districtName ?? null,
					neighbourhoodName: data.address.neighbourhoodName ?? null,
					zipCode: data.address.zipCode ?? null,
					street: data.address.street ?? null,
					buildingName: data.address.buildingName ?? null,
					lat: data.address.lat ?? null,
					lng: data.address.lng ?? null,
					addressInputType: data.address.addressInputType ?? 'fullTextAddress',
					countryID: null,
					cityID: null
				},
				person: data.person,
				email: data.email,
				servingRadius: data.servingRadius,
				phone: data.phone,
				status: 'active'
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					setBranch && setBranch(response.data.ID);
					dispatch(updateDynamicListData(DYNAMICLISTS.BRANCHES));
					dispatch(getPickupLocationCountries({ invalidate: true }));
					handleCloseModal();
					showSuccess();
				}
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.F010') {
					showError({
						header: 'nameAlreadyExists',
						message: 'error.F010'
					});
				} else {
					AppLogger.error(error);
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const defaultColSpan = {
		md: 6
	};

	return (
		<Modal maxWidth="medium" name={modalName} fullwidth className="modal-branch">
			<ModalTitle>
				<Typography variant="p1">addBranch</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="branchName" isLoading={isLoading} required>
						<StringInput name="name" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="senderName" isLoading={isLoading} required>
						<StringInput name="person" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="senderEmail" isLoading={isLoading} required>
						<StringInput name="email" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="mobileNumber" isLoading={isLoading} required>
						<PhoneNumber
							onChange={(value) => {
								setValue('phone', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('phone')}
							name="phone"
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="servingRadiusKM" isLoading={isLoading} required>
						<IntegerInput unit="km" name="servingRadius" />
					</FormItem>

					<AddressGroupV2
						formFieldName="address"
						isLoading={isLoading}
						restrictions={
							planName !== 'EnterprisePackage'
								? {
										country: {
											code: planName === 'FreePackage' ? Country : allowedCountries
										}
									}
								: null
						}
					/>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					label="button.add"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default BranchFormshort;
